import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, Square, Loader2, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_DURATION_SEC = 240; // 4 Minuten Hardcap


/**
 * Mikrofon-Recorder mit Live-Counter, Pegel-Anzeige und Whisper-Upload.
 * onTranscript(text)  → Callback wenn fertig
 */
export default function PkiVoiceRecorder({ onTranscript, disabled, compact = false }) {
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const tickRef = useRef(null);

  const cleanup = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {});
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
  };

  useEffect(() => () => cleanup(), []);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Mikrofon wird vom Browser nicht unterstützt.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Live-Pegel via Web Audio API
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tickLevel = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setLevel(Math.min(1, avg / 128));
        rafRef.current = requestAnimationFrame(tickLevel);
      };
      tickLevel();

      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleUpload;
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
      setSeconds(0);
      tickRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_DURATION_SEC) {
            stopRecording();
            return MAX_DURATION_SEC;
          }
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(
        err?.name === "NotAllowedError"
          ? "Mikrofon-Zugriff verweigert. Bitte im Browser erlauben."
          : "Mikrofon konnte nicht gestartet werden."
      );
      cleanup();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
    setPaused(false);
  };

  /** Pause die Aufnahme (Sekundenzähler + MediaRecorder pausieren). */
  const pauseRecording = () => {
    const mr = recorderRef.current;
    if (!mr || mr.state !== "recording") return;
    try { mr.pause(); } catch { /* noop */ }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setPaused(true);
  };

  /** Aufnahme fortsetzen. */
  const resumeRecording = () => {
    const mr = recorderRef.current;
    if (!mr || mr.state !== "paused") return;
    try { mr.resume(); } catch { /* noop */ }
    tickRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_DURATION_SEC) {
          stopRecording();
          return MAX_DURATION_SEC;
        }
        return s + 1;
      });
    }, 1000);
    setPaused(false);
  };

  const handleUpload = async () => {
    cleanup();
    setLevel(0);
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    chunksRef.current = [];
    if (blob.size < 1024) {
      toast.error("Aufnahme zu kurz. Bitte mind. 1 Sekunde sprechen.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", blob, "diktat.webm");
      const r = await axios.post(`${API}/pflege-ki/transcribe`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = r.data?.transcript || "";
      if (text) {
        onTranscript(text);
        toast.success(`Diktat erkannt (${text.length} Zeichen).`);
      } else {
        toast.error("Diktat erkannt, aber leer.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Diktat-Transkription fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  if (compact) {
    // Kompakter Inline-Modus für SIS-Textareas · Mobile-first: min 44px Tap-Ziel
    return (
      <span className="inline-flex items-center gap-2 flex-wrap" data-testid="pki-voice-recorder">
        {!recording && !uploading && (
          <button type="button" onClick={startRecording} disabled={disabled}
            data-testid="pki-voice-start"
            className="inline-flex items-center gap-1.5 rounded-full border border-cream-300 bg-white text-terra hover:border-terra hover:bg-peach-50 font-semibold disabled:opacity-50 min-h-[44px] px-4 text-[13.5px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[11.5px]"
            title="Diktat aufnehmen">
            <Mic className="h-4 w-4 sm:h-3 sm:w-3" /> Diktat
          </button>
        )}
        {recording && !paused && (
          <>
            <button type="button" onClick={pauseRecording}
              data-testid="pki-voice-pause"
              className="inline-flex items-center gap-1.5 rounded-full border border-terra text-terra bg-white font-semibold min-h-[44px] px-4 text-[13.5px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[11.5px]"
              title="Pause">
              <Pause className="h-4 w-4 sm:h-3 sm:w-3 fill-current" /> Pause
            </button>
            <button type="button" onClick={stopRecording}
              data-testid="pki-voice-stop"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-darkred text-white font-semibold animate-pulse min-h-[44px] px-4 text-[13.5px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[11.5px]">
              <Square className="h-4 w-4 sm:h-3 sm:w-3 fill-current" /> Stop · {mm}:{ss}
            </button>
          </>
        )}
        {recording && paused && (
          <>
            <button type="button" onClick={resumeRecording}
              data-testid="pki-voice-resume"
              className="inline-flex items-center gap-1.5 rounded-full bg-terra text-white font-semibold min-h-[44px] px-4 text-[13.5px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[11.5px]"
              title="Fortsetzen">
              <Play className="h-4 w-4 sm:h-3 sm:w-3 fill-current" /> Weiter
            </button>
            <button type="button" onClick={stopRecording}
              data-testid="pki-voice-stop"
              className="inline-flex items-center gap-1.5 rounded-full bg-sand-700 text-white font-semibold min-h-[44px] px-4 text-[13.5px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[11.5px]">
              <Square className="h-4 w-4 sm:h-3 sm:w-3 fill-current" /> Stop · {mm}:{ss}
            </button>
          </>
        )}
        {uploading && (
          <span className="inline-flex items-center gap-1.5 text-sand-700 min-h-[44px] sm:min-h-0 text-[13.5px] sm:text-[11.5px]"
            data-testid="pki-voice-uploading">
            <Loader2 className="h-4 w-4 sm:h-3 sm:w-3 animate-spin" /> Whisper …
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="pki-voice-recorder">
      {!recording && !uploading && (
        <Button onClick={startRecording} disabled={disabled}
          data-testid="pki-voice-start"
          variant="outline"
          className="rounded-full h-11 px-5 border-terra text-terra hover:bg-peach-50">
          <Mic className="h-4 w-4 mr-2" /> Diktat aufnehmen
        </Button>
      )}
      {recording && !paused && (
        <>
          <Button onClick={pauseRecording}
            data-testid="pki-voice-pause"
            variant="outline"
            className="rounded-full h-11 px-5 border-terra text-terra hover:bg-peach-50">
            <Pause className="h-4 w-4 mr-2 fill-current" /> Pause
          </Button>
          <Button onClick={stopRecording}
            data-testid="pki-voice-stop"
            className="rounded-full h-11 px-5 bg-brand-darkred hover:bg-brand-darkred/90 text-white animate-pulse">
            <Square className="h-4 w-4 mr-2 fill-current" /> Stop · {mm}:{ss}
          </Button>
          <div className="h-2 w-32 bg-cream-200 rounded-full overflow-hidden"
            data-testid="pki-voice-level">
            <div className="h-full bg-terra transition-all duration-100"
              style={{ width: `${Math.round(level * 100)}%` }} />
          </div>
        </>
      )}
      {recording && paused && (
        <>
          <Button onClick={resumeRecording}
            data-testid="pki-voice-resume"
            className="rounded-full h-11 px-5 bg-terra hover:bg-terra/90 text-white">
            <Play className="h-4 w-4 mr-2 fill-current" /> Fortsetzen
          </Button>
          <Button onClick={stopRecording}
            data-testid="pki-voice-stop"
            variant="outline"
            className="rounded-full h-11 px-5 border-sand-500 text-sand-700 hover:bg-cream-100">
            <Square className="h-4 w-4 mr-2 fill-current" /> Stop · {mm}:{ss}
          </Button>
          <span className="text-[12px] text-sand-500 italic">pausiert</span>
        </>
      )}
      {uploading && (
        <span className="inline-flex items-center gap-2 text-[13px] text-sand-700"
          data-testid="pki-voice-uploading">
          <Loader2 className="h-4 w-4 animate-spin" /> Whisper transkribiert …
        </span>
      )}
    </div>
  );
}
